import React, { useState, useEffect } from 'react';
import {
  Plus, Trash2, RefreshCw, X, Tag, Loader2,
  ArrowRight, ChevronDown, ChevronUp, Copy, Check, Shield, Pencil,
  CheckCircle2, XCircle, Inbox,
} from 'lucide-react';
import Layout from '../components/Layout';
import { PageHeader, Button, EmptyState, Spinner, ErrorMessage } from '../components/ui';
import { webhooksApi, oauthApi } from '../lib/api';
import { extractJsonPaths } from '../lib/webhooks';
import type { Webhook, OAuthConnection, CFTag } from '../lib/api';
import type { JsonPathField } from '../lib/webhooks';
import { Editor } from '@monaco-editor/react';
import type { WebhookLog } from '../types/webhook';

// ClickFunnels contact fields that can be mapped to
const CF_CONTACT_FIELDS = [
  { key: 'email_address', label: 'Email Address', required: true },
  { key: 'first_name', label: 'First Name' },
  { key: 'last_name', label: 'Last Name' },
  { key: 'phone_number', label: 'Phone Number' },
  { key: 'time_zone', label: 'Time Zone' },
  { key: 'fb_url', label: 'Facebook URL' },
  { key: 'twitter_url', label: 'Twitter URL' },
  { key: 'instagram_url', label: 'Instagram URL' },
  { key: 'linkedin_url', label: 'LinkedIn URL' },
  { key: 'website_url', label: 'Website URL' },
] as const;

export default function Webhooks() {
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [connections, setConnections] = useState<OAuthConnection[]>([]);
  const [selectedConnection, setSelectedConnection] = useState<string>('');
  const [newWebhookName, setNewWebhookName] = useState('');
  const [samplePayload, setSamplePayload] = useState(
    '{\n  "email": "example@test.com",\n  "name": "John Doe"\n}'
  );
  const [availableFields, setAvailableFields] = useState<JsonPathField[]>([]);
  const [fieldMappings, setFieldMappings] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [editingNameId, setEditingNameId] = useState<string | null>(null);
  const [editingNameValue, setEditingNameValue] = useState('');
  const [webhookLogs, setWebhookLogs] = useState<WebhookLog[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);

  // Tag state
  const [tags, setTags] = useState<CFTag[]>([]);
  const [selectedTagId, setSelectedTagId] = useState<string>('');
  const [selectedTagName, setSelectedTagName] = useState<string>('');
  const [loadingTags, setLoadingTags] = useState(false);

  // Webhook secret state
  const [webhookSecret, setWebhookSecret] = useState<string>('');
  const [hasExistingSecret, setHasExistingSecret] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  const loadWebhookLogs = async (webhookId: string) => {
    setLoadingLogs(true);
    try {
      const response = await webhooksApi.getLogs(webhookId, 10);
      if (response.success && response.data) {
        const logData = response.data as unknown as { logs: WebhookLog[] };
        const logs = logData.logs || [];
        setWebhookLogs(logs);
        if (logs.length > 0) {
          setSamplePayload(JSON.stringify(logs[0].payload, null, 2));
        } else {
          setSamplePayload('{\n  "email": "example@test.com",\n  "name": "John Doe"\n}');
        }
      }
    } catch (err) {
      console.error('Error loading webhook logs:', err);
    } finally {
      setLoadingLogs(false);
    }
  };

  useEffect(() => {
    loadWebhooks();
    loadConnections();
  }, []);

  const loadConnections = async () => {
    try {
      const response = await oauthApi.getConnections();
      if (response.success && response.data) {
        const connData = response.data as unknown as { connections: OAuthConnection[] };
        setConnections((connData.connections || []).sort((a, b) => b.createdAt - a.createdAt));
      }
    } catch (err) {
      console.error('Error loading connections:', err);
    }
  };

  const loadTagsForConnection = async (connectionId: string) => {
    if (!connectionId) {
      setTags([]);
      return;
    }
    setLoadingTags(true);
    try {
      const response = await oauthApi.getTags(connectionId);
      if (response.success && response.data) {
        const tagData = response.data as unknown as { tags: CFTag[] };
        setTags(tagData.tags || []);
      } else {
        setTags([]);
      }
    } catch (err) {
      console.error('Error loading tags:', err);
      setTags([]);
    } finally {
      setLoadingTags(false);
    }
  };

  useEffect(() => {
    if (selectedConnection && expandedId) {
      loadTagsForConnection(selectedConnection);
    }
  }, [selectedConnection, expandedId]);

  useEffect(() => {
    try {
      const payload = JSON.parse(samplePayload);
      const paths = extractJsonPaths(payload);
      setAvailableFields(paths);
    } catch {
      // ignore parse errors
    }
  }, [samplePayload]);

  const loadWebhooks = async () => {
    try {
      const response = await webhooksApi.list();
      if (response.success && response.data) {
        const data = response.data as unknown as { webhooks: Webhook[] };
        setWebhooks(data.webhooks || []);
      } else {
        setError(response.message || 'Failed to load webhooks');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load webhooks');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleExpand = (webhook: Webhook) => {
    if (expandedId === webhook.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(webhook.id);
    setFieldMappings(webhook.fieldMappings || {});
    setSelectedConnection(webhook.connectionId || '');
    setSelectedTagId(webhook.selectedTagId || '');
    setSelectedTagName(webhook.selectedTagName || '');
    setHasExistingSecret(!!webhook.webhookSecret);
    setWebhookSecret('');
    setShowSecret(false);
    setSaveSuccess(false);
    setError(null);
    setWebhookLogs([]);
    loadWebhookLogs(webhook.id);
  };

  const handleSaveMappings = async (webhookId: string) => {
    setSaving(true);
    setSaveSuccess(false);
    setError(null);
    try {
      // Send webhookSecret: new value to set, empty string to clear, undefined to leave unchanged
      const secretParam = webhookSecret !== ''
        ? webhookSecret
        : (!hasExistingSecret ? '' : undefined);
      const response = await webhooksApi.updateMappings(
        webhookId,
        fieldMappings,
        selectedConnection || null,
        selectedTagId || null,
        selectedTagName || null,
        secretParam
      );
      if (!response.success) {
        throw new Error(response.message || 'Failed to save configuration');
      }
      await loadWebhooks();
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleRenameSave = async (webhookId: string) => {
    const trimmed = editingNameValue.trim();
    if (!trimmed) return;
    try {
      const response = await webhooksApi.update(webhookId, trimmed);
      if (response.success) {
        setWebhooks(webhooks.map((w) => w.id === webhookId ? { ...w, name: trimmed } : w));
        setEditingNameId(null);
      } else {
        setError(response.message || 'Failed to rename webhook');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rename webhook');
    }
  };

  const handleCopyUrl = (webhook: Webhook) => {
    const url = webhooksApi.generateUrl(webhook.token);
    navigator.clipboard.writeText(url);
    setCopiedId(webhook.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleAddWebhook = async () => {
    if (!newWebhookName.trim()) {
      setError('Please enter a webhook name');
      return;
    }
    try {
      const response = await webhooksApi.create(newWebhookName, {});
      if (response.success && response.data) {
        const whData = response.data as unknown as { webhook: Webhook };
        setWebhooks([whData.webhook, ...webhooks]);
        setNewWebhookName('');
        setShowAddModal(false);
        setError(null);
      } else {
        throw new Error(response.message || 'Failed to create webhook');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create webhook');
    }
  };

  const handleDeleteWebhook = async (id: string) => {
    if (confirm('Are you sure you want to delete this webhook?')) {
      try {
        const response = await webhooksApi.delete(id);
        if (!response.success) throw new Error(response.message || 'Failed to delete webhook');
        setWebhooks(webhooks.filter((w) => w.id !== id));
        if (expandedId === id) setExpandedId(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to delete webhook');
      }
    }
  };

  const handleToggleWebhook = async (id: string) => {
    try {
      const response = await webhooksApi.toggle(id);
      if (response.success && response.data) {
        setWebhooks(webhooks.map((w) => w.id === id ? { ...w, active: response.data!.active } : w));
      } else {
        throw new Error(response.message || 'Failed to toggle webhook');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle webhook');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Spinner />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageHeader
        eyebrow="Inbound"
        title="Webhooks"
        description="Receive lead data and map it to ClickFunnels contact fields."
        actions={
          <Button icon={Plus} onClick={() => setShowAddModal(true)}>
            Add webhook
          </Button>
        }
      />

      {webhooks.length === 0 ? (
        <EmptyState
          icon={Inbox}
          title="No webhooks configured"
          description="Add a webhook endpoint to receive real-time updates for specific events."
          action={
            <Button icon={Plus} onClick={() => setShowAddModal(true)}>
              Add your first webhook
            </Button>
          }
        />
      ) : (
        <div className="ds-card overflow-hidden">
          {webhooks.map((webhook) => {
            const isExpanded = expandedId === webhook.id;
            const url = webhooksApi.generateUrl(webhook.token);
            const mappedCount = Object.values(webhook.fieldMappings || {}).filter(Boolean).length;

            return (
              <div key={webhook.id} className="border-t border-line-2 first:border-t-0">
                {/* Webhook Row */}
                <div className="px-5 py-4 flex items-center justify-between">
                  <div className="flex items-center space-x-4 flex-1 min-w-0">
                    {/* Status dot */}
                    <div className="flex items-center flex-shrink-0">
                      <div
                        className={`w-3 h-3 rounded-full ${webhook.active ? 'bg-success' : 'bg-ink-3'}`}
                      />
                    </div>

                    {/* Name + URL + badges */}
                    <div className="min-w-0 flex-1">
                      {editingNameId === webhook.id ? (
                        <div className="flex items-center space-x-1">
                          <input
                            type="text"
                            value={editingNameValue}
                            onChange={(e) => setEditingNameValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleRenameSave(webhook.id);
                              if (e.key === 'Escape') setEditingNameId(null);
                            }}
                            autoFocus
                            className="ds-input !h-8 !py-1 text-[13px] font-semibold max-w-xs"
                          />
                          <button
                            onClick={() => handleRenameSave(webhook.id)}
                            className="p-1 text-success hover:bg-success-soft rounded-[8px]"
                            title="Save"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setEditingNameId(null)}
                            className="p-1 text-ink-3 hover:bg-surface-2 rounded-[8px]"
                            title="Cancel"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center flex-wrap gap-x-2 gap-y-1 group">
                          <span className="font-semibold text-ink">{webhook.name}</span>
                          <button
                            onClick={() => {
                              setEditingNameId(webhook.id);
                              setEditingNameValue(webhook.name);
                            }}
                            className="p-0.5 text-ink-3 hover:text-ink-2 opacity-0 group-hover:opacity-100 transition-opacity rounded"
                            title="Rename"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          {mappedCount > 0 && (
                            <span className="ds-badge ds-badge-accent">
                              {mappedCount} fields mapped
                            </span>
                          )}
                          {webhook.selectedTagName && (
                            <span className="ds-badge ds-badge-neutral">
                              <Tag className="w-3 h-3" />
                              {webhook.selectedTagName}
                            </span>
                          )}
                          {webhook.webhookSecret && (
                            <span className="ds-badge ds-badge-success">
                              <Shield className="w-3 h-3" />
                              Verified
                            </span>
                          )}
                        </div>
                      )}
                      <div className="flex items-center space-x-2 mt-1">
                        <span className="text-[12px] font-mono text-ink-3 truncate">{url}</span>
                        <button
                          onClick={() => handleCopyUrl(webhook)}
                          className="flex-shrink-0 p-1 text-ink-3 hover:text-ink-2 hover:bg-surface-2 rounded-[8px]"
                          title="Copy URL"
                        >
                          {copiedId === webhook.id ? (
                            <Check className="w-4 h-4 text-success" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                        <span className="text-[11px] text-ink-3">
                          {new Date(webhook.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-1 ml-4 flex-shrink-0">
                    <button
                      onClick={() => handleToggleWebhook(webhook.id)}
                      className="relative w-10 h-[22px] rounded-full transition focus:outline-none focus:ring-2 focus:ring-accent-soft"
                      style={{ background: webhook.active ? 'var(--accent)' : 'var(--ink-3)' }}
                      title={webhook.active ? 'Deactivate' : 'Activate'}
                    >
                      <span
                        className="absolute top-[3px] left-[3px] w-4 h-4 rounded-full bg-white shadow transition-transform"
                        style={{ transform: webhook.active ? 'translateX(18px)' : 'none' }}
                      />
                    </button>
                    <button
                      onClick={() => handleDeleteWebhook(webhook.id)}
                      className="p-1.5 text-danger hover:bg-danger-soft rounded-[8px]"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleToggleExpand(webhook)}
                      className={`p-1.5 rounded-[8px] transition-colors ${
                        isExpanded
                          ? 'text-accent-ink bg-accent-soft'
                          : 'text-ink-3 hover:bg-surface-2'
                      }`}
                      title={isExpanded ? 'Collapse' : 'Configure'}
                    >
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5" />
                      ) : (
                        <ChevronDown className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Accordion Panel */}
                {isExpanded && (
                  <div className="bg-surface-2 border-t border-line p-5">
                    {/* Connection & Tag Row */}
                    <div className="grid grid-cols-2 gap-6 mb-5">
                      <div>
                        <label className="ds-label">
                          ClickFunnels Connection
                        </label>
                        {connections.length === 0 ? (
                          <p className="text-[13px] text-ink-3 italic py-2">
                            No connections available. Add one in Settings.
                          </p>
                        ) : (
                          <select
                            value={selectedConnection}
                            onChange={(e) => {
                              setSelectedConnection(e.target.value);
                              setSelectedTagId('');
                              setSelectedTagName('');
                              setTags([]);
                            }}
                            className="ds-input"
                          >
                            <option value="">Select a connection...</option>
                            {connections.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.connectionName || c.workspaceName || 'ClickFunnels'}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>

                      <div>
                        <label className="ds-label flex items-center gap-1">
                          <Tag className="w-4 h-4" />
                          Apply Tag to Contact
                        </label>
                        {!selectedConnection ? (
                          <p className="text-[13px] text-ink-3 italic py-2">
                            Select a connection first
                          </p>
                        ) : loadingTags ? (
                          <div className="flex items-center py-2 text-[13px] text-ink-3">
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            Loading tags...
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <select
                              value={selectedTagId}
                              onChange={(e) => {
                                const tag = tags.find((t) => t.id === e.target.value);
                                setSelectedTagId(e.target.value);
                                setSelectedTagName(tag?.name || '');
                              }}
                              className="ds-input flex-1"
                            >
                              <option value="">No tag</option>
                              {tags.map((tag) => (
                                <option key={tag.id} value={tag.id}>
                                  {tag.name}
                                </option>
                              ))}
                            </select>
                            <button
                              onClick={() => loadTagsForConnection(selectedConnection)}
                              className="p-2 text-ink-3 hover:text-ink-2 hover:bg-surface rounded-[10px]"
                              title="Refresh tags"
                            >
                              <RefreshCw className={`w-4 h-4 ${loadingTags ? 'animate-spin' : ''}`} />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Webhook Secret */}
                    <div className="mb-5">
                      <label className="ds-label flex items-center gap-1">
                        <Shield className="w-4 h-4" />
                        Webhook Secret (Signature Verification)
                      </label>
                      <p className="text-[12px] text-ink-3 mb-2">
                        If your source (e.g. Facebook Ads) sends a signature header, paste the shared secret here to verify incoming requests.
                      </p>
                      <div className="flex items-center space-x-2 max-w-lg">
                        <div className="relative flex-1">
                          <input
                            type={showSecret ? 'text' : 'password'}
                            value={webhookSecret}
                            onChange={(e) => setWebhookSecret(e.target.value)}
                            placeholder={hasExistingSecret ? '••••••••  (secret configured — enter new value to replace)' : 'Paste webhook secret...'}
                            className="ds-input pr-16"
                          />
                          <button
                            type="button"
                            onClick={() => setShowSecret(!showSecret)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-[12px] text-ink-3 hover:text-ink-2"
                          >
                            {showSecret ? 'Hide' : 'Show'}
                          </button>
                        </div>
                        {hasExistingSecret && (
                          <button
                            type="button"
                            onClick={() => {
                              setWebhookSecret('');
                              setHasExistingSecret(false);
                            }}
                            className="px-3 py-2 text-[12px] text-danger border border-line rounded-[10px] hover:bg-danger-soft"
                            title="Remove the existing secret"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      {hasExistingSecret && !webhookSecret && (
                        <p className="mt-1 text-[12px] text-success flex items-center gap-1">
                          <Check className="w-3 h-3" />
                          Secret is configured. Incoming requests will be verified.
                        </p>
                      )}
                    </div>

                    {/* Payload + Field Mapping */}
                    <div className="grid grid-cols-2 gap-6">
                      {/* Payload Viewer */}
                      <div>
                        <h4 className="font-display text-[13px] font-semibold text-ink-2 mb-2">
                          Incoming Payload
                        </h4>
                        <div className="border border-line rounded-[11px] bg-surface overflow-hidden">
                          <Editor
                            height="350px"
                            defaultLanguage="json"
                            value={samplePayload}
                            onChange={(value) => setSamplePayload(value || '')}
                            options={{
                              readOnly: true,
                              minimap: { enabled: false },
                              lineNumbers: 'on',
                              scrollBeyondLastLine: false,
                              wordWrap: 'on',
                              fontSize: 13,
                            }}
                          />
                          <div className="text-[12px] text-ink-3 p-2 border-t border-line">
                            Latest payload received. Detected fields shown on the right.
                          </div>
                        </div>
                      </div>

                      {/* Field Mapping */}
                      <div>
                        <h4 className="font-display text-[13px] font-semibold text-ink-2 mb-2">
                          Map to ClickFunnels Contact Fields
                          {Object.values(fieldMappings).filter(Boolean).length > 0 && (
                            <span className="ml-2 text-[12px] text-success font-normal">
                              ({Object.values(fieldMappings).filter(Boolean).length} mapped)
                            </span>
                          )}
                        </h4>
                        <div className="border border-line rounded-[11px] bg-surface max-h-[390px] overflow-auto">
                          {CF_CONTACT_FIELDS.map((cfField) => (
                            <div
                              key={cfField.key}
                              className={`flex items-center px-3 py-2.5 border-t border-line-2 first:border-t-0 ${
                                fieldMappings[cfField.key] ? 'bg-success-soft' : 'hover:bg-surface-2'
                              }`}
                            >
                              <div className="w-[130px] flex-shrink-0">
                                <span className="text-[13px] font-medium text-ink">
                                  {cfField.label}
                                </span>
                                {'required' in cfField && cfField.required && (
                                  <span className="text-danger ml-0.5">*</span>
                                )}
                              </div>
                              <ArrowRight className="w-4 h-4 text-ink-3 mx-2 flex-shrink-0" />
                              <select
                                value={fieldMappings[cfField.key] || ''}
                                onChange={(e) => {
                                  const newMappings = { ...fieldMappings };
                                  if (e.target.value) {
                                    newMappings[cfField.key] = e.target.value;
                                  } else {
                                    delete newMappings[cfField.key];
                                  }
                                  setFieldMappings(newMappings);
                                }}
                                className="ds-input flex-1 !h-9 font-mono text-[12px]"
                              >
                                <option value="">— not mapped —</option>
                                {availableFields.map((field) => (
                                  <option key={field.path} value={field.path}>
                                    {field.path}
                                    {field.value !== undefined && field.value !== null
                                      ? ` = "${String(field.value).substring(0, 30)}"`
                                      : ''}
                                  </option>
                                ))}
                              </select>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Info banners */}
                    {selectedConnection && selectedTagName && (
                      <div className="mt-4 p-3 bg-violet-soft border border-line rounded-[11px]">
                        <p className="text-[13px] text-violet">
                          Contacts will be upserted to ClickFunnels and tagged with
                          <strong> &quot;{selectedTagName}&quot;</strong>.
                        </p>
                      </div>
                    )}

                    {error && expandedId === webhook.id && (
                      <div className="mt-4 p-3 bg-danger-soft border border-line rounded-[11px]">
                        <p className="text-[13px] text-danger"><ErrorMessage message={error} /></p>
                      </div>
                    )}

                    {saveSuccess && (
                      <div className="mt-4 p-3 bg-success-soft border border-line rounded-[11px]">
                        <p className="text-[13px] text-success">Configuration saved successfully.</p>
                      </div>
                    )}

                    {/* Footer actions */}
                    <div className="mt-5 flex justify-end gap-2.5">
                      <button
                        onClick={() => setExpandedId(null)}
                        className="ds-btn ds-btn-ghost"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleSaveMappings(webhook.id)}
                        disabled={saving}
                        className="ds-btn ds-btn-primary disabled:opacity-50"
                      >
                        {saving ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Check className="w-4 h-4" />
                        )}
                        {saving ? 'Saving...' : 'Save configuration'}
                      </button>
                    </div>

                    {/* Recent Logs */}
                    <div className="mt-6 border-t border-line pt-5">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-display text-[13px] font-semibold text-ink">
                          Recent Logs
                          {webhookLogs.length > 0 && (
                            <span className="ml-2 text-[12px] font-normal text-ink-3">
                              (last {webhookLogs.length})
                            </span>
                          )}
                        </h4>
                        <button
                          onClick={() => loadWebhookLogs(webhook.id)}
                          disabled={loadingLogs}
                          className="text-[12px] text-ink-3 hover:text-ink-2 inline-flex items-center"
                        >
                          <RefreshCw className={`w-3 h-3 mr-1 ${loadingLogs ? 'animate-spin' : ''}`} />
                          Refresh
                        </button>
                      </div>

                      {loadingLogs ? (
                        <div className="flex items-center justify-center py-8">
                          <Spinner className="!h-5 !w-5" />
                        </div>
                      ) : webhookLogs.length === 0 ? (
                        <div className="text-center py-8">
                          <Inbox className="w-8 h-8 text-ink-3 mx-auto mb-2" />
                          <p className="text-[13px] text-ink-3">No logs yet</p>
                          <p className="text-[12px] text-ink-3 mt-1">Incoming webhook payloads will appear here</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {webhookLogs.map((log) => {
                            const isLogExpanded = expandedLogId === log.id;
                            const matchedEntries = log.matchedData ? Object.entries(log.matchedData) : [];

                            return (
                              <div
                                key={log.id}
                                className="border border-line rounded-[11px] overflow-hidden"
                              >
                                {/* Log row header */}
                                <button
                                  onClick={() => setExpandedLogId(isLogExpanded ? null : log.id)}
                                  className={`w-full px-4 py-3 flex items-center justify-between text-left hover:bg-surface-2 ${
                                    isLogExpanded ? 'bg-surface-2' : 'bg-surface'
                                  }`}
                                >
                                  <div className="flex items-center space-x-3 min-w-0">
                                    {log.status === 'success' ? (
                                      <CheckCircle2 className="w-4 h-4 text-success flex-shrink-0" />
                                    ) : (
                                      <XCircle className="w-4 h-4 text-danger flex-shrink-0" />
                                    )}
                                    <span className="text-[12px] text-ink-3 whitespace-nowrap">
                                      {new Date(log.createdAt).toLocaleString()}
                                    </span>
                                    <span className={`ds-badge ${
                                      log.status === 'success' ? 'ds-badge-success' : 'ds-badge-danger'
                                    }`}>
                                      {log.status}
                                    </span>
                                    {matchedEntries.length > 0 && (
                                      <span className="text-[12px] text-ink-2 truncate">
                                        {matchedEntries.find(([k]) => k === 'email_address')?.[1] ||
                                         matchedEntries.find(([k]) => k === 'first_name')?.[1] ||
                                         `${matchedEntries.length} fields matched`}
                                      </span>
                                    )}
                                    {log.errorMessage && (
                                      <span className="text-[12px] text-danger truncate">
                                        {log.errorMessage}
                                      </span>
                                    )}
                                  </div>
                                  {isLogExpanded ? (
                                    <ChevronUp className="w-4 h-4 text-ink-3 flex-shrink-0" />
                                  ) : (
                                    <ChevronDown className="w-4 h-4 text-ink-3 flex-shrink-0" />
                                  )}
                                </button>

                                {/* Expanded detail */}
                                {isLogExpanded && (
                                  <div className="border-t border-line bg-surface">
                                    {/* Error message */}
                                    {log.errorMessage && (
                                      <div className="px-4 py-3 bg-danger-soft border-b border-line">
                                        <p className="text-[13px] font-medium text-danger">Error</p>
                                        <p className="text-[13px] text-danger mt-1">{log.errorMessage}</p>
                                      </div>
                                    )}

                                    <div className="grid grid-cols-2 divide-x divide-line">
                                      {/* Received payload */}
                                      <div className="p-4">
                                        <div className="flex items-center justify-between mb-2">
                                          <p className="text-[11px] font-semibold text-ink-3 uppercase tracking-wide">Received Payload</p>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              navigator.clipboard.writeText(JSON.stringify(log.payload, null, 2));
                                            }}
                                            className="text-[12px] text-ink-3 hover:text-ink-2 inline-flex items-center"
                                          >
                                            <Copy className="w-3 h-3 mr-1" />
                                            Copy
                                          </button>
                                        </div>
                                        <pre className="text-[12px] bg-[var(--code-bg)] text-[var(--code-ink)] border border-line rounded-[11px] p-3 overflow-auto max-h-[300px] whitespace-pre-wrap break-all font-mono leading-relaxed">
                                          {JSON.stringify(log.payload, null, 2)}
                                        </pre>
                                      </div>

                                      {/* Sent to CF */}
                                      <div className="p-4">
                                        <div className="flex items-center justify-between mb-2">
                                          <p className="text-[11px] font-semibold text-ink-3 uppercase tracking-wide">Sent to ClickFunnels</p>
                                          {matchedEntries.length > 0 && (
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                navigator.clipboard.writeText(JSON.stringify(log.matchedData, null, 2));
                                              }}
                                              className="text-[12px] text-ink-3 hover:text-ink-2 inline-flex items-center"
                                            >
                                              <Copy className="w-3 h-3 mr-1" />
                                              Copy
                                            </button>
                                          )}
                                        </div>
                                        {matchedEntries.length === 0 ? (
                                          <p className="text-[12px] text-ink-3 italic py-2">No data mapped</p>
                                        ) : (
                                          <div className="space-y-1.5">
                                            {matchedEntries.map(([field, value]) => (
                                              <div key={field} className="flex items-start bg-surface-2 rounded-[8px] px-3 py-2">
                                                <span className="text-[12px] font-medium text-ink-2 w-[140px] flex-shrink-0">
                                                  {CF_CONTACT_FIELDS.find((f) => f.key === field)?.label || field}
                                                </span>
                                                <span className="text-[12px] text-ink break-all font-mono">{value}</span>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    </div>

                                    {/* Log metadata */}
                                    <div className="px-4 py-2 bg-surface-2 border-t border-line flex items-center space-x-4 text-[11px] text-ink-3 font-mono">
                                      <span>Log ID: {log.id}</span>
                                      <span>Payload size: {JSON.stringify(log.payload).length} bytes</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add Webhook Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="ds-card w-full max-w-lg shadow-card animate-fade">
            <div className="flex items-center justify-between p-6 border-b border-line">
              <h3 className="font-display text-[18px] font-bold text-ink">Add New Webhook</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-ink-3 hover:text-ink-2 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <label className="ds-label">
                  Webhook Name
                </label>
                <input
                  type="text"
                  value={newWebhookName}
                  onChange={(e) => setNewWebhookName(e.target.value)}
                  placeholder="My Webhook"
                  className="ds-input"
                />
              </div>
              <p className="text-[13px] text-ink-3">
                A unique webhook URL will be generated automatically.
              </p>
              {error && (
                <div className="p-4 bg-danger-soft border border-line rounded-[11px]">
                  <p className="text-[13px] text-danger"><ErrorMessage message={error} /></p>
                </div>
              )}
            </div>
            <div className="bg-surface-2 px-6 py-4 rounded-b-[15px] flex justify-end gap-2.5">
              <button
                onClick={() => setShowAddModal(false)}
                className="ds-btn ds-btn-ghost"
              >
                Cancel
              </button>
              <button
                onClick={handleAddWebhook}
                className="ds-btn ds-btn-primary"
              >
                <Plus className="w-4 h-4" />
                Add Webhook
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
